/**
 * PAWTENANT-BRANDED-PORTAL-HOSTS-LIVE-001 — client half of the host router.
 *
 * The Edge Middleware re-hosts HARD loads. In-app navigation never reaches the
 * edge, so a <Link>/navigate() to a route another portal owns (a customer
 * opening /verify, a provider signing in on the admin host, an admin landing
 * on /company from the provider host, pawtenant.com header links once legacy
 * redirects are on) is re-hosted here, with the same table as the edge
 * (src/lib/portalHosts.ts). Path, query and #fragment are kept; the
 * destination is always one of the five allowlisted LIVE hosts.
 *
 * A session does not move with the visitor: each host keeps its own sign-in,
 * so a re-hosted protected page asks for a sign-in once on its own host.
 */
import { useLayoutEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { carriesAuthCredential, redirectTarget } from "@/lib/portalHosts";

function targetUrl(pathname: string, search: string, hash: string): string | null {
  if (typeof window === "undefined") return null;
  if (carriesAuthCredential(search)) return null;
  const target = redirectTarget(window.location.hostname, pathname);
  if (!target) return null;
  return `https://${target.host}${pathname}${search}${hash}`;
}

/** Mounted once inside each router: re-hosts any route this host does not serve. */
export default function PortalHostGuard() {
  const { pathname, search, hash } = useLocation();
  useLayoutEffect(() => {
    const url = targetUrl(pathname, search, hash);
    if (url) window.location.replace(url);
  }, [pathname, search, hash]);
  return null;
}

/**
 * Catch-all for the dedicated portal shells: a route owned by another host is
 * re-hosted; anything else falls back to this portal's own landing page.
 */
export function PortalCatchAll({ fallback }: { fallback: string }) {
  const { pathname, search, hash } = useLocation();
  const url = targetUrl(pathname, search, hash);
  useLayoutEffect(() => {
    if (url) window.location.replace(url);
  }, [url]);
  if (url) return null;
  return <Navigate to={fallback} replace />;
}
